import { hasStylesToRemove } from "@azu-business/editor-v3";
import { AreYouSureModal } from "components/common/modals/AreYouSureModal";
import { PopupAlertModal } from "components/common/modals/PopupAlertModal";
import { eSingleSource, UPDATE_HIERARCHY } from "contexts/hierarchy/interface";
import { HierarchyContext } from "contexts/HierarchyContext";
import { getExternalLists } from "contexts/tlf/getExternalLists";
import { getExternalSingles } from "contexts/tlf/getExternalSingles";
import { CLOSE_MODAL_WINDOW, UPDATE_TLF } from "contexts/tlf/interface";
import { TlfContext } from "contexts/TlfContext";
import { isTlfItemReadDto, TlfItemReadDto } from "functions/api/tlfItem/TlfItemReadDto";
import { ReactNode, useContext, useState, useTransition } from "react";
import { STYLES_TO_CLEAN, STYLES_TO_DELETE } from "../../config";
import { getHierarchyLinksDescriptorsForStyledReplacements } from "./remove-style-functions/getHierarchyLinksForStyledReplacements";
import { hasStylesInReplacementValues } from "./remove-style-functions/hasStylesInReplacementValues";
import { hasStylesToRemoveInLists } from "./remove-style-functions/hasStylesToRemoveInLists";
import { hasStylesToRemoveInSingles } from "./remove-style-functions/hasStylesToRemoveInSingles";
import { hasStylesToRemoveInTlf } from "./remove-style-functions/hasStylesToRemoveInTlf";
import { StyleRemovalType } from "./remove-style-functions/interface";
import { removeStylesFromExternalLists } from "./remove-style-functions/removeStylesFromExternalLists";
import { removeStylesFromSingles } from "./remove-style-functions/removeStylesFromSingles";
import { removeStylesFromTlf } from "./remove-style-functions/removeStylesFromTlf";

interface RemoveStylesModalProps {
  /**
   * The level at which to remove styles (Tlf, HierarchySingles, or HierarchyLists)
   */
  styleRemovalLevel: StyleRemovalType;
  /**
   * Callback function invoked when the modal should be closed (optional)
   */
  onClose?: () => void;
}

/**
 * Modal component for confirming and executing the removal of Optional and Notes styles.
 * Provides a unified interface for style removal at different levels of the application:
 * - Tlf: Removes styles from TLF content including titles, footnotes, body, and singles
 * - HierarchySingles: Removes styles from hierarchy-level single replacements
 * - HierarchyLists: Removes styles from hierarchy-level replacement lists
 *
 * @param props - Component props
 * @param props.styleRemovalLevel - The level at which to remove styles
 * @param props.onClose - Callback function invoked when the modal should be closed
 */
export const RemoveStylesModal = ({
  styleRemovalLevel,
  onClose,
}: RemoveStylesModalProps): ReactNode => {
  const tlfContext = useContext(TlfContext);
  const hierarchyContext = useContext(HierarchyContext);
  const [isPending, startTransition] = useTransition();
  const tlfData: TlfItemReadDto | null = isTlfItemReadDto(tlfContext?.tlfData)
    ? tlfContext.tlfData
    : null;

  // TLF-level data (only needed for TlfLevel)
  const externalSinglesForTiFo =
    styleRemovalLevel === "Tlf" && tlfData && hierarchyContext && hierarchyContext.hierarchy
      ? getExternalSingles(
          tlfData.tocNumber,
          tlfData.releaseStatus,
          tlfData.domain,
          tlfData.singles,
          hierarchyContext.hierarchy.level,
          hierarchyContext.hierarchy.singles,
          hierarchyContext.hierarchy.domainReplacements,
          hierarchyContext.parentTemplates.singles,
          hierarchyContext.parentTemplates.domainReplacements,
        )
      : [];

  const externalLists =
    styleRemovalLevel === "Tlf" && tlfData && hierarchyContext && hierarchyContext.hierarchy
      ? getExternalLists(
          tlfData.primaryHierarchyId,
          tlfData.primaryHierarchyLevel,
          tlfData.releaseStatus,
          hierarchyContext.hierarchy.lists,
          hierarchyContext.parentTemplates.lists,
        )
      : [];

  // Collect arrays of external items with styles for detailed warning
  const externalSinglesWithStyles = externalSinglesForTiFo.filter(
    (single) =>
      single.source !== eSingleSource.tlfItem &&
      hasStylesToRemove(single.newText, [...STYLES_TO_DELETE, ...STYLES_TO_CLEAN]),
  );

  const externalListsWithStyles = externalLists.filter((list) =>
    hasStylesInReplacementValues(list.newTexts),
  );

  /**
   * Handles closing the RemoveStylesModal.
   * - For TlfContext: dispatches CLOSE_MODAL_WINDOW to close modal via context.
   * - For HierarchyContext: relies on the onClose prop from parent to close modal.
   */
  const handleClose = () => {
    if (styleRemovalLevel === "Tlf") {
      tlfContext?.dispatch({
        operation: CLOSE_MODAL_WINDOW,
        calledFrom: "RemoveStylesModal:onClose",
      });
    }
    // For HierarchyContext, rely on onClose prop from parent
    else onClose?.();
  };

  /**
   * Processes TLF content to remove Optional and Notes styles from titles, footnotes, page by,
   * body content, and singles. Promotes external singles with styles to TLF-level singles.
   */
  const handleRemoveStylesFromTlf = (): void => {
    // istanbul ignore else
    if (tlfData) {
      const styleRemovedData = removeStylesFromTlf(tlfData, externalSinglesForTiFo, externalLists);

      // Standards Notes and Programmer Notes are intentionally not processed
      tlfContext?.dispatch({
        operation: UPDATE_TLF,
        tlfData: styleRemovedData,
        calledFrom: "RemoveStylesModal:RemoveStyles",
      });
    }
  };

  /**
   * Handles style removal for hierarchy-level singles
   */
  const handleRemoveStylesFromHierarchySingles = (): void => {
    // istanbul ignore else
    if (hierarchyContext?.hierarchy) {
      const newSingles = removeStylesFromSingles(hierarchyContext.hierarchy.singles);

      hierarchyContext.dispatch({
        operation: UPDATE_HIERARCHY,
        hierarchy: {
          singles: newSingles,
        },
      });
    }
  };

  /**
   * Handles style removal for hierarchy-level lists
   */
  const handleRemoveStylesFromHierarchyLists = (): void => {
    // istanbul ignore else
    if (hierarchyContext?.hierarchy) {
      const newLists = removeStylesFromExternalLists(hierarchyContext.hierarchy.lists);

      hierarchyContext.dispatch({
        operation: UPDATE_HIERARCHY,
        hierarchy: {
          lists: newLists,
        },
      });
    }
  };

  /**
   * Main handler that delegates to the appropriate function based on replacement level.
   * Uses startTransition to mark the operation as a non-urgent update, allowing React
   * to keep the UI responsive during potentially long-running style removal operations.
   * The modal will automatically close when the transition completes via requestStatus.
   */
  const handleRemoveStyles = (): void => {
    startTransition(() => {
      switch (styleRemovalLevel) {
        case "Tlf":
          handleRemoveStylesFromTlf();
          break;
        case "HierarchySingles":
          handleRemoveStylesFromHierarchySingles();
          break;
        case "HierarchyLists":
          handleRemoveStylesFromHierarchyLists();
          break;
      }
    });
  };

  /**
   * Determines whether styles exist that need to be removed.
   * Uses lazy initialization to calculate only once at mount time,
   * preventing recalculation after styles are removed.
   */
  const [hasStylesToRemoveInData] = useState(() =>
    styleRemovalLevel === "Tlf"
      ? hasStylesToRemoveInTlf(tlfData, externalSinglesForTiFo, externalLists)
      : styleRemovalLevel === "HierarchySingles"
        ? !!hierarchyContext?.hierarchy &&
          hasStylesToRemoveInSingles(hierarchyContext.hierarchy.singles)
        : !!hierarchyContext?.hierarchy &&
          hasStylesToRemoveInLists(hierarchyContext.hierarchy.lists),
  );

  /**
   * Generates the appropriate message based on replacement level
   */
  const getMessage = (): ReactNode => {
    switch (styleRemovalLevel) {
      case "Tlf":
        return (
          <>
            <p>
              This will convert Optional (green) to black and remove Notes (blue) from Titles, Page
              by, Body and Footnotes
            </p>
            {(externalSinglesWithStyles.length > 0 || externalListsWithStyles.length > 0) &&
              hierarchyContext?.hierarchy && (
                <div className="fw-bold">
                  <p className="mb-1">
                    Warning: This will also embed styled replacements into the TLF. It is strongly
                    recommended to remove the styles from the hierarchy replacements before
                    proceeding.
                  </p>
                  {getHierarchyLinksDescriptorsForStyledReplacements({
                    hierarchy: hierarchyContext.hierarchy,
                    parentTemplates: hierarchyContext.parentTemplates,
                    hierarchyNodes: hierarchyContext.hierarchyNodes,
                    externalSinglesWithStyles,
                    externalListsWithStyles,
                  }).map((desc, idx) => (
                    <div key={idx}>
                      <a
                        href={
                          desc.type === "singles"
                            ? `${desc.baseUrl}/Replacements`
                            : `${desc.baseUrl}/Lists`
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {`View ${desc.hierarchyName} ${desc.type}`}
                      </a>
                    </div>
                  ))}
                </div>
              )}
          </>
        );
      case "HierarchySingles":
        return (
          <p>
            This action will convert Optional (green) to black text and remove Notes (blue) text
            from all single replacements in this hierarchy level.
          </p>
        );
      case "HierarchyLists":
        return (
          <p>
            This action will convert Optional (green) to black text and remove Notes (blue) text
            from all replacement lists in this hierarchy level.
          </p>
        );
    }
  };

  return !hasStylesToRemoveInData ? (
    <PopupAlertModal
      id="no-styles-to-remove-modal"
      show={true}
      title="No styles to remove"
      message="There are no Optional (green) or Notes (blue) styles present in this content."
      onClose={handleClose}
    />
  ) : (
    <AreYouSureModal
      id="remove-styles-modal"
      show={true}
      title="Remove styles"
      message={getMessage()}
      onOk={handleRemoveStyles}
      onClose={handleClose}
      requestStatus={{ requesting: isPending, error: false }}
    />
  );
};

RemoveStylesModal.displayName = "RemoveStylesModal";
